import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API, getToken } from "../lib/api";
import { SECTIONS, EMPTY_COS } from "../lib/fields";
import SocPicker from "../components/SocPicker";
import AppLayout from "../components/AppLayout";
import { toast } from "sonner";

export default function CosForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_COS);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [savedId, setSavedId] = useState(isNew ? null : id);
  const [sponsors, setSponsors] = useState([]);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/library/sponsors").then(({ data }) => setSponsors(data)).catch(() => {});
    api.get("/library/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) {
      api.get("/cos/latest-sponsor").then(({ data }) => {
        if (data?.sponsor_licence_number || data?.sponsor_name) {
          setForm((prev) => ({
            ...prev,
            sponsor_licence_number: prev.sponsor_licence_number || data.sponsor_licence_number || "",
            sponsor_name: prev.sponsor_name || data.sponsor_name || "",
          }));
        }
      }).catch(() => {});
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/cos/${id}`);
        setForm({ ...EMPTY_COS, ...data });
      } catch {
        toast.error("Failed to load certificate");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const applySponsor = (sid) => {
    const s = sponsors.find((x) => x.id === sid);
    if (!s) return;
    setForm((p) => ({
      ...p,
      sponsor_licence_number: s.sponsor_licence_number || p.sponsor_licence_number,
      sponsor_name: s.sponsor_name || p.sponsor_name,
      work_address: s.work_address || p.work_address,
      work_city: s.work_city || p.work_city,
      work_area_district: s.work_area_district || p.work_area_district,
      work_postcode: s.work_postcode || p.work_postcode,
      paye_reference_number: s.paye_reference_number || p.paye_reference_number,
    }));
    toast.success(`Sponsor applied`);
  };

  const applyJob = (jid) => {
    const j = jobs.find((x) => x.id === jid);
    if (!j) return;
    setForm((p) => ({
      ...p,
      job_type: j.job_type || p.job_type,
      job_title: j.job_title || p.job_title,
      summary_of_job_description: j.summary_of_job_description || p.summary_of_job_description,
      gross_salary: j.gross_salary || p.gross_salary,
      salary_for: j.salary_for || p.salary_for,
      total_weekly_hours: j.total_weekly_hours || p.total_weekly_hours,
    }));
    toast.success(`Job applied`);
  };

  const save = async () => {
    setSaving(true);
    try {
      let out;
      if (savedId) {
        const { data } = await api.put(`/cos/${savedId}`, form);
        out = data;
      } else {
        const { data } = await api.post("/cos", form);
        out = data;
        setSavedId(data.id);
        window.history.replaceState(null, "", `/cos/${data.id}`);
      }
      setForm({ ...EMPTY_COS, ...out });
      toast.success("Saved");
      return out;
    } catch {
      toast.error("Failed to save");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    const saved = savedId ? { id: savedId } : await save();
    if (!saved) return;
    try {
      const res = await fetch(`${API}/cos/${saved.id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = form.certificate_number ? `CoS-${form.certificate_number}.pdf` : `CoS.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download");
    }
  };

  const preview = async () => {
    try {
      const res = await fetch(`${API}/cos/preview-pdf`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Preview failed");
    }
  };

  if (loading) {
    return (
      <AppLayout breadcrumb={[{ label: "View CoS", to: "/" }, { label: "Loading" }]}>
        <p>Loading…</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      activeMenu={isNew && !savedId ? "create" : "view"}
      breadcrumb={[
        { label: "Sponsorship management system", to: "/" },
        { label: "Workers", to: "/cos" },
        { label: "View CoS", to: "/cos" },
        { label: isNew && !savedId ? "Create new" : "CoS details" },
      ]}
    >
      <h1>{isNew && !savedId ? "Create a Certificate of Sponsorship" : "CoS details"}</h1>
      <p>
        Complete the sections below. Fields marked <span style={{ color: "#a30000" }}>*</span>{" "}
        are shown on the generated PDF. Use the <strong>Load a saved sponsor</strong> and{" "}
        <strong>Load a saved job</strong> dropdowns to pre-fill from your library.
      </p>

      {SECTIONS.map((sec) => {
        const picker =
          sec.id === "cos_status" && sponsors.length > 0
            ? { label: "Load a saved sponsor", testid: "sponsor-picker", options: sponsors, onSelect: applySponsor, format: (s) => `${s.sponsor_name || "(no name)"} — ${s.sponsor_licence_number || "-"}` }
            : sec.id === "employment" && jobs.length > 0
            ? { label: "Load a saved job", testid: "job-picker", options: jobs, onSelect: applyJob, format: (j) => `${j.job_title || "(no title)"} — ${j.job_type || "-"}`.slice(0, 90) }
            : null;

        return (
          <div key={sec.id} className="sms-panel" data-testid={`section-${sec.id}`}>
            <div className="sms-panel-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{sec.title}</span>
              {picker && (
                <select
                  data-testid={picker.testid}
                  className="sms-select"
                  style={{ maxWidth: 300, fontWeight: "normal" }}
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) picker.onSelect(e.target.value); e.target.value = ""; }}
                >
                  <option value="">{picker.label}…</option>
                  {picker.options.map((o) => (
                    <option key={o.id} value={o.id}>{picker.format(o)}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="sms-panel-body">
              {sec.fields.map((f) => {
                const isSoc = f.name === "job_type";
                return (
                  <div key={f.name} className="sms-field-row">
                    <label className="sms-field-label" htmlFor={f.name}>{f.label}:</label>
                    <div>
                      {isSoc ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <SocPicker
                            value={form.job_type}
                            onPick={({ label, title }) =>
                              setForm((p) => ({
                                ...p,
                                job_type: label,
                                job_title: p.job_title || title,
                              }))
                            }
                          />
                          <input
                            id={f.name}
                            data-testid={`field-${f.name}`}
                            value={form[f.name] ?? ""}
                            onChange={(e) => setField(f.name, e.target.value)}
                            placeholder="Or type manually"
                            className="sms-input"
                          />
                        </div>
                      ) : f.textarea ? (
                        <textarea
                          id={f.name}
                          data-testid={`field-${f.name}`}
                          value={form[f.name] ?? ""}
                          onChange={(e) => setField(f.name, e.target.value)}
                          rows={f.rows || 3}
                          className="sms-textarea"
                        />
                      ) : f.type === "select" ? (
                        <select
                          id={f.name}
                          data-testid={`field-${f.name}`}
                          value={form[f.name] || ""}
                          onChange={(e) => setField(f.name, e.target.value)}
                          className="sms-select"
                        >
                          <option value="">Select…</option>
                          {f.options.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={f.name}
                          data-testid={`field-${f.name}`}
                          value={form[f.name] ?? ""}
                          onChange={(e) => setField(f.name, e.target.value)}
                          className="sms-input"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="sms-actionbar">
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/cos" className="sms-btn secondary">Back</Link>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" data-testid="preview-btn" onClick={preview} className="sms-btn secondary">
            Preview
          </button>
          <button
            type="button"
            data-testid="save-btn"
            onClick={save}
            disabled={saving}
            className="sms-btn secondary"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" data-testid="download-pdf-btn" onClick={download} className="sms-btn">
            Download PDF
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
